import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, Paper, useMediaQuery, Badge } from '@mui/material';
import {
  Message as MessageIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Menu as MenuIcon } from '@mui/icons-material';
import ChatList from './ChatList';
import ChatView from './ChatView';
import SearchView from './SearchView';
import Sidebar from './Sidebar';
import { useConversation } from './services/index';
import StyledTooltip from './StyledTooltip';
import { useSelector } from 'react-redux';
import { getAgentData } from '../../services/util';
import { store } from '../../context/store';
import { handleAgentSubUserChat } from '../../context/digitalSlice';
import { MODES } from '../../constants';

const ChatContent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('recent');
  const { activeConversation, users, getConversation, sendMessage, activeConversationLoading, conversations, setConversations, setActiveConversationId, getParticipants, searchLoading } = useConversation(isOpen, isSearchOpen);
  const isWidthLessThan460 = useMediaQuery('(max-width: 460px)');
  const isMobile = useMediaQuery('(max-width: 650px)');
  const newMessageFromWebSocket = useSelector((state) => state.digital.agentSubUserLatestMsg);

  const isConnected = useSelector((state) => state.toolbar.isWebsocketConnected);
  const mode = useSelector((state) => state.toolbar.agent.mode)
  const unreadCount = useMemo(() => {
    try {
      if (conversations && Array.isArray(conversations) && conversations.length > 0) {
        const unreadConversations = conversations.filter((conversation) => conversation.unreadCount > 0);
        return unreadConversations.reduce((acc, conversation) => acc + conversation.unreadCount, 0);
      }
      return 0;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }, [conversations]);

  const styles = useMemo(() => ({
    chatButton: {
      position: 'fixed',
      bottom: (isMobile && (mode === MODES.DIGITAL || mode === MODES.BELNDED)) ? '60px' : '16px',
      right: '4px',
      bgcolor: isConnected ? 'primary.main' : '#E65B5C',
      color: 'white',
      '&:hover': {
        bgcolor: isConnected ? 'primary.dark' : '#E65B5C',
        transform: 'scale(1.1)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      },
      width: (isMobile && (mode === MODES.DIGITAL || mode === MODES.BELNDED)) ? '30px' : '42px',
      height: (isMobile && (mode === MODES.DIGITAL || mode === MODES.BELNDED)) ? '30px' : '42px',
      zIndex: 9999,
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'pop-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      '@keyframes pop-in': {
        '0%': {
          opacity: 0,
          transform: 'scale(0.5)',
        },
        '100%': {
          opacity: 1,
          transform: 'scale(1)',
        },
      },
      '& .Mui-disabled': {
        opacity: 1,
        cursor: 'not-allowed',
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
        pointerEvents: "auto"

      },
    },
    chatWindow: {
      position: 'fixed',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 10001,
    },
    header: {
      bgcolor: 'primary.main',
      color: 'white',
      p: isWidthLessThan460 ? '4px 0px' : '4px 12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'background-color 0.3s ease',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: isWidthLessThan460 ? 0 : 1,
    },
    headerActions: {
      display: 'flex',
      gap: 1,
    },
    contentContainer: {
      flex: 1,
      display: 'flex',
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    headerButton: {
      color: 'inherit',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'scale(1.1)',
        background: 'rgba(255, 255, 255, 0.15)',
      },
    },
    enterAnimation: {
      animation: 'slide-up 0.3s ease-out',
      '@keyframes slide-up': {
        from: {
          opacity: 0,
          transform: 'translateY(20px)',
        },
        to: {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
    },
    expandAnimation: {
      animation: 'expand 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes expand': {
        from: {
          transform: 'scale(0.95)',
        },
        to: {
          transform: 'scale(1)',
        },
      },
    },

    disconnectedContainer: {
      bgcolor: '#E65B5C',
      color: 'white',
      fontSize: '11px',
      fontWeight: 'bold',
      textAlign: 'center',
      pt: '1px',
    },
    chatIcon: {
      width: (isMobile && (mode === MODES.DIGITAL || mode === MODES.BELNDED)) ? '16px' : 'auto',
      height: (isMobile && (mode === MODES.DIGITAL || mode === MODES.BELNDED)) ? '16px' : 'auto',
    },
  }), [isWidthLessThan460, isMobile, mode, isConnected]);

  useEffect(() => {
    const controller = new AbortController();

    try {
      if (newMessageFromWebSocket && newMessageFromWebSocket?.senderId) {
        let participants = getParticipants(newMessageFromWebSocket.senderId);
        if (participants && Array.isArray(participants) && participants.length === 0) {
          const participant = {
            id: parseInt(newMessageFromWebSocket.senderId),
            name: newMessageFromWebSocket.senderName,
            role: 'admin',
            status: 'online',
          }
          participants = [participant];
        }
        console.log("participants", participants);
        if (participants && Array.isArray(participants) && participants.length > 0) {
          const newConversation = {
            id: parseInt(newMessageFromWebSocket.senderId),
            messages: [newMessageFromWebSocket],
            title: newMessageFromWebSocket.senderName,
            lastMessage: newMessageFromWebSocket,
            unreadCount: 1,
            participants: participants,
            isGroup: false,
            createdAt: new Date(),
          }
          let conversationExists = Boolean(activeConversation?.id);
          if (!conversationExists) {
            try {
              if (conversations && Array.isArray(conversations) && conversations.length > 0) {
                const isExistingConversation = conversations.find((conversation) => conversation.id === parseInt(newMessageFromWebSocket.senderId));
                if (isExistingConversation) {
                  conversationExists = true;
                }
              }
            } catch (err) {
              console.error(err);
            }
          }
          console.log("conversationExists", conversationExists);
          if (!conversationExists) {
            getConversation(newConversation, controller.signal, 'websocket');
          } else {
            setConversations((prev) => {
              let newConversations = (prev && Array.isArray(prev) && prev.length > 0) ? [...prev] : [];
              if (newConversations && Array.isArray(newConversations) && newConversations.length > 0) { // existing conversation
                const isExistingConversation = newConversations.find((conversation) => conversation.id === parseInt(newMessageFromWebSocket.senderId));
                console.log("isExistingConversation", isExistingConversation);
                if (isExistingConversation) {
                  newConversations = newConversations.map((conversation) => {
                    if (conversation.id === parseInt(newMessageFromWebSocket.senderId)) {
                      const sortedMessages = [...conversation.messages, newMessageFromWebSocket].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                      const isActiveConversation = (conversation.id === activeConversation?.id);
                      return {
                        ...conversation,
                        messages: sortedMessages,
                        unreadCount: (isActiveConversation && isOpen) ? 0 : conversation?.unreadCount ? conversation?.unreadCount + 1 : 1,
                        lastMessage: sortedMessages[sortedMessages.length - 1],
                        createdAt: new Date(),
                      };
                    }
                    return conversation;
                  });
                } else {
                  newConversations.push(newConversation);
                }
              } else {
                newConversations = [newConversation];
              }
              console.log("newConversations", newConversations);
              return newConversations;
            });
          }
        } else {
          console.log("no participants");
        }
      } else {
        console.log("no message");
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (newMessageFromWebSocket && newMessageFromWebSocket?.senderId) {
        store.dispatch(handleAgentSubUserChat(''))
      }

    }
  }, [newMessageFromWebSocket]);

  useEffect(() => {
    if (isWidthLessThan460 && isExpanded) {
      setIsExpanded(false);
    }
  }, [isWidthLessThan460, isExpanded]);

  const chatWindowStyles = {
    '& .MuiAvatar-root': {
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '&:hover': {
        boxShadow: '4 4px 6px 1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      },
    },
    ...styles.chatWindow,
    display: isOpen ? 'flex' : 'none',
    ...(isExpanded
      ? {
        top: isWidthLessThan460 ? '50px' : '90px',
        left: isWidthLessThan460 ? '10px' : '100px',
        right: '8px',
        bottom: isWidthLessThan460 ? '50px' : '70px',
        minHeight: '300px',
        minWidth: '235px',
        ...styles.expandAnimation,
      }
      : {
        bottom: isWidthLessThan460 ? '50px' : '70px',
        right: '8px',
        width: isWidthLessThan460 ? 'calc(100vw - 20px)' : '400px',
        height: isWidthLessThan460 ? 'calc(100vh - 120px)' : 'calc(100vh - 160px)',
        minHeight: '300px',
        maxHeight: (isExpanded || isWidthLessThan460) ? 'none' : '500px',
        minWidth: '235px',
        ...styles.enterAnimation,
      }),
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    setIsSidebarExpanded(!isExpanded);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleSelectConversation = (conversation) => {
    try {
      // if this is a new conversation, then add this to the conversations array
      if (!conversations.find((c) => c.id === conversation.id)) {
        setConversations([...conversations, conversation]);
      }
      setActiveConversationId(conversation.id);
      setIsSearchOpen(false);
    } catch (error) {
      console.error(error);
    }
  };


  const handleSendMessage = async (content, mediaUrl) => {


    let agentData = getAgentData();

    try {
      if (!activeConversation) return;

      const newMessage = {
        id: `msg-${activeConversation.id}-${Date.now()}`,
        senderId: agentData?.agentUniqId || '',
        senderName: agentData?.agentInfo?.agentId || '',
        content,
        timestamp: new Date(),
        mediaUrl,
        isRead: false,
        isSent: false,
      };

      const updatedConversation = {
        ...activeConversation,
        messages: [...activeConversation.messages, newMessage],
        lastMessage: newMessage,
      };

      setConversations(
        conversations.map((conv) =>
          conv.id === activeConversation.id ? updatedConversation : conv
        )
      );
      setActiveConversationId(activeConversation.id);
      sendMessage(newMessage, updatedConversation);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredConversations = useMemo(() => {
    return conversations.filter((conversation) => {
      switch (filterCategory) {
        case 'recent':
          return true;
        case 'unread':
          return conversation.unreadCount > 0;
        case 'chats':
          return !conversation.isGroup;
        case 'groups':
          return conversation.isGroup;
        default:
          return true;
      }
    }).sort((a, b) => {
      if (filterCategory === 'recent') {
        return (b.lastMessage?.timestamp?.getTime() || 0) - (a.lastMessage?.timestamp?.getTime() || 0);
      }
      return 0;
    });
  }, [conversations, filterCategory]);

  const toggleSidebar = () => {
    setIsSidebarExpanded((prev) => !prev);
  };

  return (
    <>
      <StyledTooltip title={isConnected ? "Chat is connected" : "Chat is disconnected, try refresh or re-login to reconnect"} placement="right">
        <IconButton
          onClick={toggleChat}
          sx={styles.chatButton}
          aria-label={isOpen ? "Close chat" : "Open chat"}
        >
          <MessageIcon sx={styles.chatIcon} />
          {isNaN(unreadCount) ? null : <Badge sx={{
            top: -22,
            right: 0,
          }} badgeContent={unreadCount} color="error" />}
        </IconButton>

      </StyledTooltip>

      <Paper sx={chatWindowStyles}>
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            {activeConversation && filteredConversations.length > 0 && <StyledTooltip title={isSidebarExpanded ? "Collapse Sidebar" : "Expand Sidebar"} placement="right">
              <IconButton
                onClick={toggleSidebar}
                size="small"
                disabled={isSearchOpen}
                sx={{
                  color: 'white',
                  borderRadius: '50%',
                  ml: .5,
                  background: isSidebarExpanded ? 'rgb(90, 135, 218)' : 'transparent',
                  '&:hover': {
                    background: 'rgb(90, 135, 218)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}>
                <MenuIcon />
              </IconButton>
            </StyledTooltip>}
            <Typography variant="subtitle1" fontWeight="medium" color="white" ml={1}>
              Conversations
            </Typography>
          </Box>
          <Box sx={styles.headerActions}>
            <IconButton
              size="small"
              onClick={toggleSearch}
              sx={{
                color: 'inherit',
                background: isSearchOpen ? 'rgb(90, 135, 218)' : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgb(90, 135, 218)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <SearchIcon />
            </IconButton>
            {!isWidthLessThan460 && <IconButton
              size="small"
              onClick={toggleExpand}
              sx={styles.headerButton}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>}
            <IconButton
              size="small"
              onClick={toggleChat}
              sx={{
                ...styles.headerButton,
                mr: isWidthLessThan460 ? .5 : 0,
                '&:hover': {
                  transform: 'scale(1.1) rotate(90deg)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {
          !isConnected && (
            <Box sx={{ ...styles.disconnectedContainer, ...styles.enterAnimation }}>
              Chat is disconnected, try refresh or re-login to reconnect
            </Box>
          )
        }
        <Box sx={styles.contentContainer}>
          {!isSearchOpen && activeConversation && filteredConversations.length > 0 && (
            <Sidebar
              conversations={filteredConversations}
              onSelectConversation={handleSelectConversation}
              selectedConversation={activeConversation || undefined}
              widgetExpanded={isExpanded}
              isExpanded={isSidebarExpanded}
              activeConversationLoading={activeConversationLoading}
            />
          )}

          <Box sx={styles.content}>
            {isSearchOpen ? (
              <SearchView
                searchLoading={searchLoading}
                onClose={() => setIsSearchOpen(false)}
                users={users}
                conversations={conversations || []}
                onSelectConversation={handleSelectConversation}
                isExpanded={isExpanded}
              />
            ) :// currently Chat list is not working as no api is implemented to get all chat history, it's only used to display No conversations found message 
              (!activeConversation) ? (
                <ChatList
                  conversations={filteredConversations}
                  onSelectConversation={handleSelectConversation}
                  filterCategory={filterCategory}
                  setFilterCategory={setFilterCategory}
                  isExpanded={isExpanded}
                  hideNavbar={true}
                />
              ) :

                (
                  <ChatView
                    conversation={activeConversation}
                    onSendMessage={handleSendMessage}
                    isExpanded={isExpanded}
                    loading={activeConversationLoading}
                    onBack={() => setActiveConversationId(null)}
                  />
                )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

const SubUserChatWidget = () => {
  return (
    <ChatContent />
  );
};

export default SubUserChatWidget;